import React, { useState } from "react";
import dayjs from "dayjs";
import {
  Layout,
  Form,
  Select,
  Button,
  theme,
  Typography,
  Flex,
  Row,
  Col,
  Card,
  Descriptions,
  Tag,
  Divider,
  Steps,
  Alert,
  Spin,
  notification,
} from "antd";
import {
  CheckCircleFilled,
  FileTextOutlined,
  FileDoneOutlined,
  ContainerOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

import HeaderApp from "../../shared/components/HeaderApp";
import NavigationBar from "../../shared/components/NavigationBar";
import SectionHeader from "../../shared/components/SectionHeader";
import EmptyData from "../../shared/components/EmptyData";

import { useFetchQuotes } from "../../quotes/hooks/useFetchQuotes";
import type { QuoteData } from "../../quotes/types";

import { useFetchContractById } from "../../contracts/hooks/useFetchContractById";
import { useCreateContract } from "../../contracts/hooks/useCreateContract";
import ContractFrom from "../../contracts/components/ContractFrom";
import SelectedQuoteComponent from "../../contracts/components/SelectedQuoteComponent";
import { getPaymentMethods } from "../../contracts/utils/paymentMethods";

import { useCreateBill } from "../hooks/userCreateBill";
import type { BillingItem } from "../types";

const { Text, Title } = Typography;

/* ────────────────────────────────────────────
   Small helper: label-value field
──────────────────────────────────────────── */
const FieldLabel: React.FC<{ text: string }> = ({ text }) => {
  const { token } = theme.useToken();
  return (
    <Text
      style={{
        fontSize: 11,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.07em",
        color: token.colorTextSecondary,
      }}
    >
      {text}
    </Text>
  );
};

/* ────────────────────────────────────────────
   Read-only contract data card
──────────────────────────────────────────── */
const ContractDataCard: React.FC<{ contract: any }> = ({ contract }) => {
  const { token } = theme.useToken();
  return (
    <Card
      title={
        <Flex align="center" gap={8}>
          <FileDoneOutlined style={{ color: token.colorSuccess }} />
          <Text strong>Contrato Vinculado</Text>
          <Tag color="green">#{contract.id_contract}</Tag>
        </Flex>
      }
      style={{
        borderRadius: token.borderRadiusLG,
        border: `1px solid ${token.colorSuccessBorder}`,
        background: token.colorSuccessBg,
      }}
      styles={{ body: { padding: 20 } }}
    >
      <Descriptions column={1} size="small" layout="vertical">
        <Descriptions.Item label={<FieldLabel text="Nombre" />}>
          <Text strong>{contract.contract_name}</Text>
        </Descriptions.Item>
        <Descriptions.Item label={<FieldLabel text="Métodos de Pago" />}>
          <Flex gap={4} wrap>
            {getPaymentMethods(contract).map((method) => (
              <Tag key={method} style={{ margin: 0 }}>{method}</Tag>
            ))}
          </Flex>
        </Descriptions.Item>
        <Descriptions.Item label={<FieldLabel text="Vigencia" />}>
          {dayjs(contract.start_date).format("DD/MM/YYYY")}
          {contract.end_date
            ? ` → ${dayjs(contract.end_date).format("DD/MM/YYYY")}`
            : ""}
        </Descriptions.Item>
        <Descriptions.Item label={<FieldLabel text="Parcialidades" />}>
          <Tag color={contract.partials === "Si" ? "blue" : "default"}>
            {contract.partials === "Si"
              ? `Sí — ${contract.partials_total} pagos (${contract.partials_percentage}%)`
              : "No"}
          </Tag>
        </Descriptions.Item>
      </Descriptions>
    </Card>
  );
};

/* ────────────────────────────────────────────
   Bill form — basic billing fields
──────────────────────────────────────────── */
const BillFormFields: React.FC<{ form: any }> = ({ form }) => {
  const { token } = theme.useToken();
  const iStyle: React.CSSProperties = { borderRadius: token.borderRadiusLG };

  return (
    <Form
      id="bill-create-form"
      form={form}
      layout="vertical"
      requiredMark={false}
      style={{
        border: `1px solid ${token.colorBorderSecondary}`,
        padding: 24,
        backgroundColor: token.colorBgContainer,
        borderRadius: token.borderRadiusLG,
      }}
    >
      <Flex vertical gap={16}>
        <SectionHeader title="Datos de la Factura" />

        <Form.Item
          name="typeBill"
          label={<FieldLabel text="Tipo de Comprobante" />}
          rules={[{ required: true, message: "Campo requerido" }]}
          style={{ marginBottom: 0 }}
        >
          <Select
            size="large"
            placeholder="Selecciona un tipo"
            style={iStyle}
            options={[
              { value: "I", label: "I — Ingreso" },
              { value: "E", label: "E — Egreso" },
              { value: "T", label: "T — Traslado" },
              { value: "N", label: "N — Nómina" },
              { value: "P", label: "P — Pago" },
            ]}
          />
        </Form.Item>

        <Form.Item
          name="paymentMethod"
          label={<FieldLabel text="Forma de Pago" />}
          rules={[{ required: true, message: "Campo requerido" }]}
          style={{ marginBottom: 0 }}
        >
          <Select
            size="large"
            placeholder="Selecciona forma de pago"
            style={iStyle}
            options={[
              { value: "01", label: "01 — Efectivo" },
              { value: "02", label: "02 — Cheque nominativo" },
              { value: "03", label: "03 — Transferencia electrónica" },
              { value: "04", label: "04 — Tarjeta de crédito" },
              { value: "28", label: "28 — Tarjeta de débito" },
              { value: "99", label: "99 — Por definir" },
            ]}
          />
        </Form.Item>

        <Form.Item
          name="receptor_CFDI_usage"
          label={<FieldLabel text="Uso de CFDI" />}
          rules={[{ required: true, message: "Campo requerido" }]}
          style={{ marginBottom: 0 }}
        >
          <Select
            size="large"
            placeholder="Selecciona uso de CFDI"
            style={iStyle}
            options={[
              { value: "G01", label: "G01 — Adquisición de mercancias" },
              { value: "G03", label: "G03 — Gastos en general" },
              { value: "I01", label: "I01 — Construcciones" },
              { value: "P01", label: "P01 — Por definir" },
              { value: "S01", label: "S01 — Sin efectos fiscales" },
            ]}
          />
        </Form.Item>

        <Form.Item
          name="invoiceType"
          label={<FieldLabel text="Tipo Factura" />}
          rules={[{ required: true, message: "Campo requerido" }]}
          style={{ marginBottom: 0 }}
        >
          <Select
            size="large"
            placeholder="Selecciona tipo"
            style={iStyle}
            options={[
              { value: "ingreso", label: "Ingreso" },
              { value: "egreso", label: "Egreso" },
              { value: "traslado", label: "Traslado" },
              { value: "nomina", label: "Nómina" },
              { value: "pago", label: "Pago" },
            ]}
          />
        </Form.Item>
      </Flex>
    </Form>
  );
};

/* ────────────────────────────────────────────
   Main BillCreate page
──────────────────────────────────────────── */
const BillCreate: React.FC = () => {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const [api, contextHolder] = notification.useNotification();

  const [selectedQuoteId, setSelectedQuoteId] = useState<number | null>(null);
  const [contractForm] = Form.useForm();
  const [billForm] = Form.useForm();

  const { data: quotes, isLoading: isLoadingQuotes } = useFetchQuotes();
  const selectedQuote: QuoteData | undefined = quotes?.find(
    (q: QuoteData) => q.id_quote === selectedQuoteId
  );

  // Fetch contract only if quote has one linked
  const {
    data: contract,
    isLoading: isLoadingContract,
    isFetched: contractFetched,
  } = useFetchContractById(selectedQuote?.id_contract ?? null);

  const hasContract = contractFetched && !!contract;
  const noContract =
    contractFetched && !contract && selectedQuote?.id_contract == null;

  const { mutateAsync: createContract, isPending: isCreatingContract } =
    useCreateContract();
  const { mutateAsync: createBill, isPending: isCreatingBill } =
    useCreateBill();

  /* ── current step for stepper UI ── */
  const currentStep = !selectedQuoteId ? 0 : !hasContract && !noContract ? 1 : 2;

  const handleSubmit = async () => {
    try {
      // 1. If no contract exists, validate & create one first
      if (noContract) {
        const contractValues = await contractForm.validateFields();
        const contractPayload = {
          ...contractValues,
          id_quote: selectedQuoteId,
          signing_date: contractValues.signing_date?.toISOString(),
          start_date: contractValues.start_date?.toISOString(),
          end_date: contractValues.end_date?.toISOString(),
        };
        await createContract(contractPayload);
      }

      // 2. Validate bill fields
      const billValues = await billForm.validateFields();

      const billPayload: BillingItem = {
        ...billValues,
        sender_name: "",
        sender_rfc: "",
        sender_tax_regime: "",
        receptor_name: "",
        receptor_tax_regime: "",
        receptor_rfc: "",
        receptor_address: "",
        date: new Date(),
        signature: "",
        noCertificate: "",
        certificate: "",
        total: "0",
        subTotal: "0",
        status_bill: "En Proceso",
        json_bill: JSON.stringify(billValues),
      };

      await createBill(billPayload);

      api.success({
        message: "Factura creada correctamente",
        description: "La factura fue registrada exitosamente.",
        placement: "topRight",
      });

      navigate("/facturas");
    } catch (error: any) {
      if (error?.errorFields) return; // form validation error — antd already shows inline
      api.error({
        message: "Error al crear la factura",
        description: error?.message ?? "Intenta de nuevo.",
        placement: "topRight",
      });
    }
  };

  return (
    <>
      {contextHolder}
      <HeaderApp sectionName="Nueva Factura" showBack backPath="/facturas" />

      <Layout.Content style={{ padding: "16px 16px 140px" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>

          {/* ── Progress steps ── */}
          <Steps
            current={currentStep}
            size="small"
            style={{ marginBottom: 32 }}
            items={[
              {
                title: "Cotización",
                icon: <FileTextOutlined />,
                description: "Selecciona",
              },
              {
                title: "Contrato",
                icon: <FileDoneOutlined />,
                description: "Verifica datos",
              },
              {
                title: "Factura",
                icon: <ContainerOutlined />,
                description: "Completa",
              },
            ]}
          />

          <Row gutter={[24, 24]}>
            {/* ── LEFT column: forms ── */}
            <Col xs={24} md={14}>
              <Flex vertical gap={24}>

                {/* STEP 1 — Quote selector */}
                <div
                  style={{
                    border: `1px solid ${token.colorBorderSecondary}`,
                    borderRadius: token.borderRadiusLG,
                    padding: 24,
                    background: token.colorBgContainer,
                  }}
                >
                  <Flex vertical gap={16}>
                    <SectionHeader title="1 — Selecciona una Cotización" />
                    <Select
                      size="large"
                      loading={isLoadingQuotes}
                      disabled={isLoadingQuotes}
                      placeholder="Buscar cotización…"
                      showSearch
                      optionFilterProp="label"
                      value={selectedQuoteId}
                      onChange={(val) => {
                        setSelectedQuoteId(val);
                        contractForm.resetFields();
                        billForm.resetFields();
                      }}
                      options={quotes?.map((q: QuoteData) => ({
                        value: q.id_quote,
                        label: `COT-${q.id_quote} — ${q.description_quote}`,
                      }))}
                      style={{ borderRadius: token.borderRadiusLG }}
                    />
                  </Flex>
                </div>

                {/* STEP 2 — Contract section */}
                {selectedQuote && (
                  <>
                    {isLoadingContract ? (
                      <Flex justify="center" style={{ padding: 32 }}>
                        <Spin tip="Cargando contrato…" />
                      </Flex>
                    ) : hasContract ? (
                      /* Contract exists — show read-only card */
                      <Flex vertical gap={12}>
                        <SectionHeader title="2 — Contrato Vinculado" />
                        <ContractDataCard contract={contract} />
                      </Flex>
                    ) : (
                      /* No contract — show inline ContractFrom */
                      <Flex vertical gap={12}>
                        <SectionHeader title="2 — Datos del Contrato" />
                        <Alert
                          type="warning"
                          showIcon
                          icon={<WarningOutlined />}
                          message="Esta cotización no tiene contrato"
                          description="Completa los datos del contrato antes de emitir la factura."
                          style={{ borderRadius: token.borderRadiusLG }}
                        />
                        <ContractFrom
                          form={contractForm}
                          onFinish={() => {}}
                          hideQuoteSelect
                          showDetails={false}
                          isCreating={isCreatingContract}
                        />
                      </Flex>
                    )}
                  </>
                )}

                {/* STEP 3 — Bill fields */}
                {selectedQuote && (hasContract || noContract) && (
                  <Flex vertical gap={12}>
                    <SectionHeader title="3 — Datos de la Factura" />
                    <BillFormFields form={billForm} />
                  </Flex>
                )}

                {/* ── Submit ── */}
                {selectedQuote && (hasContract || noContract) && (
                  <Button
                    type="primary"
                    size="large"
                    icon={<CheckCircleFilled />}
                    loading={isCreatingBill || isCreatingContract}
                    onClick={handleSubmit}
                    block
                    style={{
                      height: 54,
                      borderRadius: token.borderRadiusLG,
                      fontWeight: 800,
                      fontSize: 16,
                      letterSpacing: "0.01em",
                      boxShadow: `0 8px 20px ${token.colorPrimaryBg}`,
                    }}
                  >
                    {noContract
                      ? "Guardar Contrato y Crear Factura"
                      : "Crear Factura"}
                  </Button>
                )}

              </Flex>
            </Col>

            {/* ── RIGHT column: summary sidebar ── */}
            <Col xs={24} md={10}>
              <div style={{ position: "sticky", top: 24 }}>
                <Flex vertical gap={20}>
                  {selectedQuote ? (
                    <SelectedQuoteComponent selectedQuote={selectedQuote} />
                  ) : (
                    <EmptyData description="Selecciona una cotización para continuar" />
                  )}

                  {/* Mini guide card */}
                  <Card
                    size="small"
                    style={{
                      borderRadius: token.borderRadiusLG,
                      background: token.colorFillAlter,
                      border: `1px solid ${token.colorBorderSecondary}`,
                    }}
                  >
                    <Flex vertical gap={8}>
                      <Title level={5} style={{ margin: 0 }}>
                        ¿Cómo funciona?
                      </Title>
                      <Text type="secondary" style={{ fontSize: 13 }}>
                        1. Elige una cotización existente.
                      </Text>
                      <Text type="secondary" style={{ fontSize: 13 }}>
                        2. Si tiene contrato, sus datos se cargan automáticamente.
                        Si no, completa el formulario de contrato.
                      </Text>
                      <Text type="secondary" style={{ fontSize: 13 }}>
                        3. Llena los datos fiscales y crea la factura.
                      </Text>
                    </Flex>
                  </Card>
                </Flex>
              </div>
            </Col>
          </Row>
        </div>
      </Layout.Content>

      <NavigationBar activeKey="/facturas" />
    </>
  );
};

export default BillCreate;
